import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { SiteLayout } from "@/components/site/Layout";
import { Section, SectionEyebrow } from "@/components/site/Section";
import { CTABanner } from "@/components/site/CTABanner";
import { getPostBySlug } from "@/lib/blog.functions";
import { resolveLegacyRedirect } from "@/lib/legacy-redirects";
import { isTestimonialPost } from "@/lib/testimonials";
import { BRAND } from "@/lib/brand";

const storyQuery = (slug: string) =>
  queryOptions({
    queryKey: ["testimonial-story", slug],
    queryFn: async () => {
      const p = await getPostBySlug({ data: { slug } });
      if (!p || !isTestimonialPost(p.tags)) throw notFound();
      return p;
    },
  });

export const Route = createFileRoute("/$")({
  loader: async ({ params, context }) => {
    const raw = (params as { _splat?: string })._splat ?? "";
    const target = resolveLegacyRedirect("/" + raw);
    if (target) throw redirect({ href: target, statusCode: 301 });

    const slug = raw.replace(/^\/+|\/+$/g, "");
    if (!slug || slug.includes("/") || !/^[a-z0-9-]+$/.test(slug)) throw notFound();
    return context.queryClient.ensureQueryData(storyQuery(slug));
  },
  head: ({ params, loaderData }) => {
    const p: any = loaderData;
    if (!p) {
      return { meta: [{ title: "Page not found - Kristan Cole Network" }, { name: "robots", content: "noindex" }] };
    }
    const url = `https://joinkcn.com/${(params as { _splat?: string })._splat}`;
    const title = p.seo_title || `${p.title} | Kristan Cole Network`;
    const description = p.seo_description || p.excerpt || "";
    const image = p.cover_image_url;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        ...(image
          ? [
              { property: "og:image", content: image },
              { name: "twitter:image", content: image },
            ]
          : []),
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Review",
            headline: p.title,
            description,
            datePublished: p.published_at,
            itemReviewed: {
              "@type": "RealEstateAgent",
              name: BRAND.name,
              url: "https://joinkcn.com/",
            },
            author: { "@type": "Person", name: p.author_name || BRAND.name },
            publisher: { "@type": "Organization", name: BRAND.name },
            image: image ? [image] : undefined,
            mainEntityOfPage: url,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://joinkcn.com/" },
              { "@type": "ListItem", position: 2, name: "Testimonials", item: "https://joinkcn.com/testimonials" },
              { "@type": "ListItem", position: 3, name: p.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: StoryPage,
  notFoundComponent: NotFoundPage,
  errorComponent: ({ error }) => (
    <SiteLayout>
      <Section className="pt-36 md:pt-36 lg:pt-36">
        <p className="text-primary">
          {error instanceof Error ? error.message : "This page didn't load."}
        </p>
      </Section>
    </SiteLayout>
  ),
});

function NotFoundPage() {
  return (
    <SiteLayout>
      <Section className="pt-36 md:pt-36 lg:pt-36 text-center">
        <p className="eyebrow mb-4">404</p>
        <h1 className="headline-lg">Page not found</h1>
        <p className="mt-4 text-foreground/80">
          This page doesn't exist or has moved.
        </p>
        <div className="mt-8 flex justify-center gap-4">
          <Link
            to="/"
            className="inline-flex items-center bg-primary px-6 py-3 text-xs uppercase tracking-[0.18em] text-primary-foreground hover:bg-primary/90"
          >
            Return home
          </Link>
          <Link
            to="/meet-the-team"
            className="inline-flex items-center border border-border px-6 py-3 text-xs uppercase tracking-[0.18em] hover:bg-surface"
          >
            Meet the team
          </Link>
        </div>
      </Section>
    </SiteLayout>
  );
}

function StoryPage() {
  const { _splat } = Route.useParams() as { _splat: string };
  const { data: p } = useSuspenseQuery(storyQuery(_splat.replace(/^\/+|\/+$/g, "")));
  const takeaways: string[] = Array.isArray((p as any).key_takeaways)
    ? (p as any).key_takeaways
    : [];

  return (
    <SiteLayout>
      <article>
        <Section tone="dark" className="pt-36 md:pt-36 lg:pt-36 pb-12">
          <div className="max-w-3xl">
            <SectionEyebrow>Success story</SectionEyebrow>
            <h1 className="font-display font-light tracking-tight leading-[1.05] text-3xl md:text-4xl lg:text-5xl mb-6">
              {p.title}
            </h1>
            {p.excerpt ? (
              <p className="text-xl text-foreground/80 leading-relaxed">{p.excerpt}</p>
            ) : null}
          </div>
        </Section>

        {p.cover_image_url ? (
          <div className="container-edge -mt-4 mb-4">
            <img
              src={p.cover_image_url}
              alt={p.title}
              className="w-full aspect-[16/9] object-cover"
            />
          </div>
        ) : null}

        <Section tone="cream" className="pt-8">
          <div className="max-w-3xl mx-auto">
            {takeaways.length ? (
              <aside
                aria-label="Key takeaways"
                className="mb-10 border border-cream-foreground/20 bg-cream-foreground/[0.04] p-6"
              >
                <div className="text-xs uppercase tracking-[0.2em] text-cream-foreground/65 mb-3">
                  Key takeaways
                </div>
                <ul className="space-y-2 list-disc pl-5 text-cream-foreground/90">
                  {takeaways.map((t, i) => (
                    <li key={i}>{t}</li>
                  ))}
                </ul>
              </aside>
            ) : null}

            <div className="prose-blog prose-on-cream">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{p.body_markdown}</ReactMarkdown>
            </div>

            <div className="mt-16 pt-10 border-t border-cream-foreground/20">
              <Link
                to="/testimonials"
                className="text-xs uppercase tracking-[0.18em] text-cream-foreground/65 hover:text-cream-foreground"
              >
                ← All testimonials
              </Link>
            </div>
          </div>
        </Section>
      </article>

      <CTABanner cta="strategy" />
    </SiteLayout>
  );
}
